import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Toast from "../components/molecules/Toast";

type Variant = "success" | "error" | "info" | "warning";

interface ToastItem {
  id: number;
  message: string;
  variant: Variant;
}

export function useToast() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<number, number>>({});

  const remove = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: Variant = "info", durationMs = 3000) => {
      const id = Date.now();
      setToasts((t) => [...t, { id, message, variant }]);
      const t = window.setTimeout(() => {
        remove(id);
        delete timers.current[id];
      }, durationMs);
      timers.current[id] = t as unknown as number;
    },
    [remove],
  );

  useEffect(() => {
    return () => {
      for (const id in timers.current) {
        window.clearTimeout(timers.current[id]!);
      }
      timers.current = {};
    };
  }, []);

  const ToastItemView = ({
    id,
    message,
    variant,
  }: {
    id: number;
    message: string;
    variant: Variant;
  }) => {
    const [show, setShow] = useState(false);
    useEffect(() => {
      const r = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(r);
    }, []);
    return (
      <div
        className={`transform transition-all duration-200 ${show ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"} motion-reduce:transition-none motion-reduce:transform-none`}
      >
        <Toast key={id} message={message} variant={variant} />
      </div>
    );
  };

  const Toasts = () => {
    if (typeof document === "undefined") return null;
    return createPortal(
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map((t) => (
          <ToastItemView key={t.id} id={t.id} message={t.message} variant={t.variant} />
        ))}
      </div>,
      document.body,
    );
  };

  return { addToast, Toasts };
}

export default useToast;
